// ./src/utils/api/BudgetPaymentService.ts
//
// Budget payment service — payments made toward a budget item (money-OUT).
// OpResult-native, scoped to a budget item. Full single-item CRUD + bulk remove
// (array of ids in the where clause). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/budget.ts)
// =============================================================================

export const BUDGET_PAYMENT_METHOD = {
  CASH: "cash",
  CARD: "card",
  BANK_TRANSFER: "bank_transfer",
  CHEQUE: "cheque",
  OTHER: "other",
} as const;

export const BUDGET_PAYMENT_METHOD_VALUES = Object.values(BUDGET_PAYMENT_METHOD);

// =============================================================================
// GraphQL
// =============================================================================

const BUDGET_PAYMENT_FIELDS = `
  id
  budgetItemId
  eventId
  accountId
  amount
  currencyCode
  paidAt
  paymentMethod
  note
  paidByKind
  paidById
  paidByLabel
  status
  rights
  createdAt
  updatedAt
`;

const BUDGET_PAYMENT_LIST_QUERY = gql`
  query EventBudgetPaymentList($filter: EventBudgetPaymentFilter, $params: PaginationAndSorting) {
    eventBudgetPaymentList(filter: $filter, params: $params) {
      code
      data { ${BUDGET_PAYMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_PAYMENT_CREATE_MUTATION = gql`
  mutation EventBudgetPaymentCreate($params: EventBudgetPaymentInput) {
    eventBudgetPaymentCreate(params: $params) {
      code
      data { ${BUDGET_PAYMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_PAYMENT_UPDATE_MUTATION = gql`
  mutation EventBudgetPaymentUpdate($where: EventBudgetPaymentWhereInput, $params: EventBudgetPaymentInput) {
    eventBudgetPaymentUpdate(where: $where, params: $params) {
      code
      data { ${BUDGET_PAYMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_PAYMENT_REMOVE_MUTATION = gql`
  mutation EventBudgetPaymentRemove($where: EventBudgetPaymentWhereInput) {
    eventBudgetPaymentRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// Confirm a guest's self-reported "I already paid" contribution → creates the real ledger
// payment + marks the contribution confirmed, in one server-side transaction.
const BUDGET_PAYMENT_CONFIRM_CONTRIBUTION_MUTATION = gql`
  mutation EventBudgetPaymentConfirmContribution($params: EventBudgetPaymentConfirmContributionInput) {
    eventBudgetPaymentConfirmContribution(params: $params) {
      code
      data { contributionId paymentId budgetItemId amount }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventBudgetPayment schema)
// =============================================================================

export interface BudgetPayment {
  id?: string;
  budgetItemId?: string | null;
  eventId?: string | null;
  accountId?: string | null;
  amount?: number | null;
  currencyCode?: string | null;
  paidAt?: string | null;
  paymentMethod?: string | null;
  note?: string | null;
  paidByKind?: string | null;
  paidById?: string | null;
  paidByLabel?: string | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface BudgetPaymentInput {
  budgetItemId?: string | null;
  amount?: number | null;
  currencyCode?: string | null;
  paidAt?: string | null;
  paymentMethod?: string | null;
  note?: string | null;
  paidByKind?: string | null;
  paidById?: string | null;
  status?: number | null;
}

export interface BudgetPaymentFilter {
  id?: string[] | null;
  budgetItemId?: string[] | null;
  eventId?: string[] | null;
  paymentMethod?: string[] | null;
  paidByKind?: string[] | null;
  paidById?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface BudgetPaymentWhereInput {
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class BudgetPaymentService {
  static async list(
    filter?: BudgetPaymentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(BUDGET_PAYMENT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async create(params: BudgetPaymentInput): Promise<OpResult> {
    return ApiService.executeMutation(BUDGET_PAYMENT_CREATE_MUTATION, { params });
  }

  static async update(where: BudgetPaymentWhereInput, params: BudgetPaymentInput): Promise<OpResult> {
    return ApiService.executeMutation(BUDGET_PAYMENT_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(BUDGET_PAYMENT_REMOVE_MUTATION, { where: { id } });
  }

  /**
   * Confirm a guest's self-reported contribution (a `paid` event_guest_signup row) as a real
   * payment. Server-side, this creates the ledger payment (paidByKind='guest') and marks the
   * contribution confirmed in one transaction. Optional paidAt (YYYY-MM-DD) + paymentMethod.
   */
  static async confirmContribution(params: {
    contributionId: string;
    paidAt?: string | null;
    paymentMethod?: string | null;
  }): Promise<OpResult> {
    return ApiService.executeMutation(BUDGET_PAYMENT_CONFIRM_CONTRIBUTION_MUTATION, { params });
  }
}
